import { useCallback, useRef } from 'preact/hooks';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import type { Subscription } from '@/domain/Subscription';
import styles from './SubscriptionItem.module.scss';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/widgets/ProductDetailsHero/config';
import SubscriptionRadio from '@/widgets/ProductDetailsHero/drawers/SubscriptionOptionsDrawer/SubscriptionItem/SubscriptionRadio/SubscriptionRadio';

type SubscriptionItemProps = {
  productId: string;
  subscription: Subscription;
  subscriptionsFilteredByDuration: Subscription[];
  selected?: boolean;
  onSubscriptionSelected: (subscriptionId: string) => void;
  duration?: number;
  singleSubscription: boolean;
};

export default function SubscriptionItem({
  productId,
  subscription,
  subscriptionsFilteredByDuration,
  selected,
  onSubscriptionSelected,
  duration,
  singleSubscription
}: SubscriptionItemProps) {
  const { t } = useTranslations<Translations>();
  const hasSingleSubscription = subscriptionsFilteredByDuration?.length === 1;
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target as HTMLInputElement;
      onSubscriptionSelected(target.value);
    },
    [onSubscriptionSelected, subscription.id]
  );

  const isTranslationAndSubscriptionSingle =
    t(
      'productDetailsHero.drawers.subscriptionOptionsDrawer.legend.yourSubscriptionLabel'
    ) && hasSingleSubscription;

  const handleClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef.current]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        handleClick();
      }
    },
    [handleClick]
  );

  return (
    <div
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      class={styles.subscriptionItem}
      tabIndex={0}
      role="button"
    >
      {isTranslationAndSubscriptionSingle ? (
        <div class={styles.yourSubscription}>
          {t(
            'productDetailsHero.drawers.subscriptionOptionsDrawer.legend.yourSubscriptionLabel'
          )}
        </div>
      ) : null}
      <div class={styles.wrapper}>
        {!singleSubscription && (
          <>
            <h3>{subscription.title}</h3>
            <FormattedText>{subscription.description}</FormattedText>
          </>
        )}

        <div class={styles.priceDescription}>
          <strong>
            {t(
              'productDetailsHero.drawers.subscriptionOptionsDrawer.subscriptionItem.monthlyPlan',
              { monthlyCost: subscription.parameters.periodicFee }
            )}
          </strong>
          {singleSubscription && duration && (
            <p class={styles.durationLabel}>
              {t(
                'productDetailsHero.drawers.subscriptionOptionsDrawer.subscriptionDurationLabel',
                { duration }
              )}
            </p>
          )}
          {singleSubscription && subscription.description && (
            <div class={styles.description}>
              <FormattedText>{subscription.description}</FormattedText>
            </div>
          )}
          {subscription.offerDescription && (
            <div class={styles.offerDescription}>
              <FormattedText>{subscription.offerDescription}</FormattedText>
            </div>
          )}
        </div>
      </div>

      {!hasSingleSubscription && (
        <div class={styles.input}>
          <SubscriptionRadio
            id={subscription.id}
            type="radio"
            name={productId}
            value={subscription.id}
            checked={selected}
            onChange={handleChange}
            inputRef={inputRef}
          />
        </div>
      )}
    </div>
  );
}
